/**
 * Build split-service garage suggestions when no single garage covers all clusters.
 * Spec Phase 6 / §10.4.
 */

function garageCoversClusters(workshop, clusterIds = []) {
  const selected = Array.isArray(workshop?.selectedClusters)
    ? workshop.selectedClusters.map(Number)
    : [];
  // Legacy garages with no clusters still appear in discovery for any filter —
  // treat them as unable to guarantee multi-cluster coverage for split logic.
  if (!selected.length) return false;
  return clusterIds.every((id) => selected.includes(Number(id)));
}

function distanceSortKey(workshop) {
  const d = workshop?.distance;
  return d == null || Number.isNaN(Number(d)) ? Number.POSITIVE_INFINITY : Number(d);
}

function pickBestForCluster(workshops, clusterId, excludeIds = new Set()) {
  const candidates = workshops
    .filter((w) => {
      const id = String(w.id || '');
      if (excludeIds.has(id)) return false;
      return garageCoversClusters(w, [clusterId]);
    })
    .sort((a, b) => {
      const da = distanceSortKey(a);
      const db = distanceSortKey(b);
      if (da !== db) return da - db;
      return Number(b.fulfillmentScore || 0) - Number(a.fulfillmentScore || 0);
    });
  return candidates[0] || null;
}

/**
 * @param {Array} workshops - mapped NearbyGarageCard workshops
 * @param {Array<{id,label,serviceType,clusterId}>} issues - selected issue options (with clusterId)
 * @returns {{ combined: Array, splits: Array }}
 */
export function buildSplitCartSuggestions(workshops = [], issues = []) {
  const selected = (Array.isArray(issues) ? issues : [])
    .filter((i) => i && i.clusterId != null && Number(i.clusterId) >= 1);

  const uniqueByCluster = [];
  const seen = new Set();
  for (const issue of selected) {
    const cid = Number(issue.clusterId);
    if (seen.has(cid)) continue;
    seen.add(cid);
    uniqueByCluster.push(issue);
  }

  if (uniqueByCluster.length < 2) {
    return { combined: [], splits: [], multiIssue: false };
  }

  const clusterIds = uniqueByCluster.map((i) => Number(i.clusterId));
  const combined = workshops
    .filter((w) => garageCoversClusters(w, clusterIds))
    .sort((a, b) => distanceSortKey(a) - distanceSortKey(b))
    .slice(0, 8);

  if (combined.length) {
    return { combined, splits: [], multiIssue: true };
  }

  // Greedy: best garage per issue, prefer different garages
  const used = new Set();
  const legs = [];
  for (const issue of uniqueByCluster) {
    let garage = pickBestForCluster(workshops, issue.clusterId, used);
    if (!garage) {
      garage = pickBestForCluster(workshops, issue.clusterId, new Set());
    }
    if (!garage) continue;
    used.add(String(garage.id));
    legs.push({
      issueId: issue.id,
      label: issue.label,
      serviceType: issue.serviceType,
      clusterId: Number(issue.clusterId),
      garage,
    });
  }

  const distinctGarageCount = new Set(legs.map((l) => String(l.garage?.id))).size;
  const splits =
    legs.length >= 2 && distinctGarageCount >= 2
      ? [
          {
            id: legs.map((l) => `${l.clusterId}:${l.garage?.id}`).join('|'),
            title: legs.map((l) => `${l.label} by ${l.garage?.name || 'Garage'}`).join(' + '),
            legs,
            totalFromPrice: legs.reduce((sum, l) => {
              const p = Number(l.garage?.bestDeal?.price ?? l.garage?.price ?? 0);
              return sum + (Number.isFinite(p) ? p : 0);
            }, 0),
          },
        ]
      : [];

  return { combined: [], splits, multiIssue: true };
}

export { garageCoversClusters };
